<template>
  <div class="page-container post-page">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">社区帖子</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ list.length }}</b> 条</span>
        <span class="divider">|</span>
        <span>图文 <b>{{ typeCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>视频 <b>{{ typeCount(2) + typeCount(3) }}</b></span>
        <span class="divider">|</span>
        <span>留言 <b>{{ commentTotal }}</b></span>
      </div>
    </div>

    <!-- 左右分栏：左 = 筛选 + 帖子内容，右 = 操作面板 -->
    <div class="post-layout">
      <!-- ==================== 左：帖子列表 ==================== -->
      <el-card class="post-card post-list-card" shadow="never">
        <div slot="header" class="card-head">
          <span class="card-head-title">帖子内容</span>
          <span class="card-head-extra">{{ filteredList.length }} / {{ list.length }}</span>
        </div>

        <!-- 筛选 -->
        <div class="list-filter">
          <el-input
            v-model="query.keyword"
            size="small"
            clearable
            placeholder="搜索标题 / 正文 / 作者"
            prefix-icon="el-icon-search"
          />
          <div class="filter-row">
            <el-select v-model="query.type" size="small" placeholder="全部形式" clearable>
              <el-option v-for="t in POST_TYPE" :key="t.value" :label="t.label" :value="t.value" />
            </el-select>
            <el-button size="small" icon="el-icon-refresh" @click="resetFilter">重置</el-button>
          </div>
        </div>

        <!-- 帖子卡片列表 -->
        <div v-loading="loading" class="post-list">
          <div
            v-for="item in filteredList"
            :key="item.id"
            class="post-item"
            :class="{ 'is-active': current && current.id === item.id }"
            @click="selectPost(item)"
          >
            <div class="item-thumb">
              <el-image
                v-if="item.type === 1 && item.images && item.images.length"
                :src="item.images[0]"
                :preview-src-list="item.images"
                fit="cover"
                style="width: 100%; height: 100%"
              />
              <el-image
                v-else-if="item.type !== 1 && item.cover"
                :src="item.cover"
                :preview-src-list="[item.cover]"
                fit="cover"
                style="width: 100%; height: 100%"
              />
              <template v-else-if="item.type === 1">
                <i class="el-icon-picture-outline" />
                <span class="thumb-tag">纯文字</span>
              </template>
              <template v-else>
                <i class="el-icon-video-camera" />
                <span class="thumb-tag">视频</span>
              </template>
              <span v-if="item.type !== 1 && formatDuration(item.duration)" class="thumb-duration">
                {{ formatDuration(item.duration) }}
              </span>
            </div>

            <div class="item-main">
              <div class="item-title">
                <el-tag v-if="item.is_official === 1" size="mini" effect="dark" class="official-tag">官方</el-tag>
                <span class="title-text">{{ item.title || '（无标题）' }}</span>
              </div>
              <div class="item-content">{{ item.content || '（无正文）' }}</div>
              <div class="item-foot">
                <span>{{ item.nickname || '用户' }}</span>
                <span class="dot">·</span>
                <span>{{ item.created_at }}</span>
                <span class="dot">·</span>
                <span>{{ postTypeLabel(item.type) }}</span>
              </div>
            </div>

            <div class="item-right">
              <div class="item-stats">
                <span><i class="el-icon-chat-dot-square" />{{ item.comment_count }}</span>
                <span><i class="el-icon-star-on" />{{ item.like_count }}</span>
                <span><i class="el-icon-star-off" />{{ item.favorite_count }}</span>
              </div>
            </div>
          </div>

          <div v-if="!loading && !filteredList.length" class="list-empty">
            <i class="el-icon-document-delete" />
            <p>没有符合条件的帖子</p>
          </div>
        </div>
      </el-card>

      <!-- ==================== 右：操作面板 ==================== -->
      <el-card class="post-card post-action-card" shadow="never">
        <div slot="header" class="card-head">
          <span class="card-head-title">操作面板</span>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleCreate">发布帖子</el-button>
        </div>

        <!-- 未选中 -->
        <div v-if="!current" class="action-empty">
          <i class="el-icon-chat-dot-round" />
          <p class="empty-title">从左侧选择一条帖子</p>
          <p class="empty-sub">右侧直接呈现小程序端效果，可回复留言、删除帖子或删掉某条留言</p>
        </div>

        <!-- 已选中：直接把小程序端效果铺在右侧 -->
        <div v-else class="action-body">
          <div class="panel-block">
            <div class="block-title">
              <span class="block-title-text">小程序端效果</span>
              <span class="block-title-right">
                <span class="block-hint">{{ postTypeLabel(current.type) }} · {{ postMediaText(current) }}</span>
                <el-button
                  size="mini" plain class="text-danger-btn"
                  icon="el-icon-delete"
                  @click="handleDeletePost(current)"
                >删除帖子</el-button>
              </span>
            </div>

            <!-- 模拟小程序里的帖子卡片 -->
            <div class="phone-card">
              <div class="phone-body">
                <div class="phone-author">
                  <el-image
                    v-if="current.avatar"
                    :src="current.avatar"
                    fit="cover"
                    class="avatar avatar--img"
                  />
                  <span v-else class="avatar" :class="{ 'avatar--official': current.is_official === 1 }">
                    {{ shortName(current.nickname) }}
                  </span>
                  <span class="phone-author-info">
                    <span class="phone-author-name">
                      {{ current.nickname || '用户' }}
                      <el-tag v-if="current.is_official === 1" size="mini" effect="dark" class="official-tag">官方</el-tag>
                    </span>
                    <span class="phone-time">{{ current.created_at }}</span>
                  </span>
                </div>

                <h4 class="phone-title">{{ current.title || '（无标题）' }}</h4>
                <p class="phone-content">{{ current.content || '（无正文）' }}</p>

                <!-- 媒体：图文三栏 / 横版视频 16:9 / 竖版视频 3:4 -->
                <div class="phone-media">
                  <div v-if="current.type === 1" class="phone-grid">
                    <div v-for="(img, i) in (current.images || [])" :key="i" class="phone-grid-item">
                      <el-image :src="img" :preview-src-list="current.images || []" :initial-index="i" fit="cover" style="width: 100%; height: 100%" />
                    </div>
                    <div v-if="!(current.images || []).length" class="phone-media-empty">纯文字帖，无配图</div>
                  </div>
                  <div
                    v-else
                    class="phone-video"
                    :class="current.type === 2 ? 'phone-video--wide' : 'phone-video--tall'"
                    :style="coverStyle(current)"
                    @click="openVideo"
                  >
                    <i class="el-icon-video-play" />
                    <span v-if="formatDuration(current.duration)" class="phone-video-duration">
                      {{ formatDuration(current.duration) }}
                    </span>
                  </div>
                </div>

                <div v-if="current.activity_id" class="phone-tag"># {{ activityName(current.activity_id) }}</div>

                <div class="phone-stats">
                  <span><i class="el-icon-chat-dot-square" /> {{ current.comment_count }}</span>
                  <span><i class="el-icon-star-on" /> {{ current.like_count }}</span>
                  <span><i class="el-icon-star-off" /> {{ current.favorite_count }}</span>
                </div>
              </div>

              <!-- 留言区即管理区：官方回复 + 逐条可删 -->
              <div v-loading="commentsLoading" class="phone-comments">
                <div class="phone-comments-title">
                  留言 {{ comments.length }}
                  <span class="phone-comments-hint">删除后小程序端不再展示</span>
                </div>

                <!-- 官方回复 -->
                <div class="reply-box">
                  <el-input
                    v-model="replyContent"
                    type="textarea"
                    :rows="2"
                    maxlength="500"
                    show-word-limit
                    placeholder="以官方账号回复该帖子…"
                  />
                  <div class="reply-actions">
                    <span class="reply-tip">以「{{ OFFICIAL_NICKNAME }}」身份回复，发布后小程序端可见</span>
                    <el-button type="primary" size="mini" :disabled="!replyContent.trim()" @click="handleReply">回复</el-button>
                  </div>
                </div>

                <div v-for="c in comments" :key="c.id" class="phone-comment">
                  <el-image
                    v-if="c.avatar"
                    :src="c.avatar"
                    fit="cover"
                    class="avatar avatar--sm avatar--img"
                  />
                  <span v-else class="avatar avatar--sm" :class="{ 'avatar--official': c.is_official === 1 }">
                    {{ shortName(c.nickname) }}
                  </span>
                  <div class="phone-comment-main">
                    <div class="phone-comment-name">
                      {{ c.nickname || '用户' }}
                      <el-tag v-if="c.is_official === 1" size="mini" effect="dark" class="official-tag">官方</el-tag>
                      <span class="phone-comment-time">{{ c.created_at }}</span>
                    </div>
                    <div class="phone-comment-content">{{ c.content }}</div>
                    <div class="phone-comment-foot">
                      <span class="comment-like"><i class="el-icon-star-off" /> {{ c.like_count }}</span>
                      <el-button
                        type="text" size="mini" class="text-danger-btn"
                        @click="handleDeleteComment(c)"
                      >删除</el-button>
                    </div>
                  </div>
                </div>
                <div v-if="!comments.length" class="phone-comment-empty">该帖还没有留言</div>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- ==================== 发布帖子弹框 ==================== -->
    <el-dialog
      title="发布帖子"
      :visible.sync="publishVisible"
      width="720px"
      top="5vh"
      class="post-form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="publishForm" :model="publishForm" :rules="publishRules" label-width="100px" label-position="top">
        <el-alert
          :title="`将以「${OFFICIAL_NICKNAME}」的官方账号发布，社区不做审核，发布后小程序端立即可见`"
          type="info"
          :closable="false"
          show-icon
          class="form-alert"
        />

        <div class="form-section">
          <div class="form-section-title">帖子内容</div>
          <el-form-item label="内容形式">
            <div class="detect-line">
              <el-tag size="small" effect="dark">{{ publishTypeLabel }}</el-tag>
              <span class="detect-text">{{ publishDetectText }}</span>
            </div>
          </el-form-item>
          <el-form-item label="标题" prop="title">
            <el-input v-model="publishForm.title" placeholder="如：慢下来，感受生活的小美好" maxlength="128" show-word-limit />
          </el-form-item>
          <el-form-item label="正文" prop="content">
            <el-input
              v-model="publishForm.content"
              type="textarea"
              :rows="4"
              maxlength="2000"
              show-word-limit
              placeholder="输入帖子正文，展示在标题下方，小程序端按纯文本渲染"
            />
          </el-form-item>

          <!-- 图文模式：文字 / 图文，最多 6 张图集
               视频功能暂时下线（原条件：v-if="!publishForm.video_url || publishForm.images.length"，与视频互斥） -->
          <el-form-item label="配图">
            <multi-image-uploader
              v-model="publishForm.images"
              dir="post/image"
              :max="POST_MAX_IMAGES"
              :hint="`最多 ${POST_MAX_IMAGES} 张，第一张作为卡片缩略图`"
            />
          </el-form-item>

          <!-- 视频功能暂时下线：视频上传 + 视频封面（恢复时反注释，并把上面配图的 v-if 条件改回互斥写法）
          <el-form-item v-if="publishForm.video_url || !publishForm.images.length" label="视频" prop="video_url">
            <video-uploader v-model="publishForm.video_url" dir="post/video" @duration="onVideoDuration" @metadata="onVideoMeta" />
          </el-form-item>

          <el-form-item v-if="publishForm.video_url" label="视频封面（可选）">
            <image-uploader v-model="publishForm.cover" tip="上传封面" hint="留空由小程序端截取视频画面展示" />
          </el-form-item>
          -->
        </div>

        <div class="form-section">
          <div class="form-section-title">发布设置</div>
          <el-form-item label="关联活动">
            <el-select v-model="publishForm.activity_id" placeholder="不关联" clearable style="width: 100%">
              <el-option label="不关联" :value="0" />
              <el-option v-for="a in activities" :key="a.id" :label="a.name" :value="a.id" />
            </el-select>
            <span class="form-tip">选中的活动名会作为卡片上的标签展示（对应小程序发布页的「#添加活动」）</span>
          </el-form-item>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="publishVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitPublish">发 布</el-button>
      </div>
    </el-dialog>

    <!-- ==================== 视频预览弹框 ==================== -->
    <el-dialog
      :title="current ? `视频预览 · ${current.title || '（无标题）'}` : '视频预览'"
      :visible.sync="videoVisible"
      width="720px"
      top="6vh"
      append-to-body
      @closed="onVideoClosed"
    >
      <video
        v-if="videoVisible && current && current.video_url"
        :src="current.video_url"
        controls
        autoplay
        playsinline
        class="video-preview"
      />
      <div v-else class="video-preview-empty">该帖没有视频地址</div>
    </el-dialog>
  </div>
</template>

<script>
import { POST_TYPE, POST_MAX_IMAGES, OFFICIAL_USER, postTypeLabel, formatDuration, postMediaText } from '@/mock/post'
import { getActivityList } from '@/api/activity'
import { getPostList, getPostDetail, createPost, replyPost, deletePost, deletePostComment } from '@/api/post'
import ImageUploader from '@/components/ImageUploader.vue'
import MultiImageUploader from '@/components/MultiImageUploader.vue'
import VideoUploader from '@/components/VideoUploader.vue'

export default {
  name: 'CommunityPostManage',
  components: { ImageUploader, MultiImageUploader, VideoUploader },
  data() {
    return {
      loading: false,
      list: [],
      comments: [],
      activities: [],
      query: { keyword: '', type: '' },
      current: null,
      commentsLoading: false,
      replyContent: '',
      videoVisible: false,
      // 发布
      publishVisible: false,
      submitting: false,
      publishForm: this.emptyPublishForm(),
      publishRules: {
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        content: [{
          validator: (rule, value, callback) => {
            if (!String(value || '').trim()) callback(new Error('请输入正文'))
            else callback()
          },
          trigger: 'blur'
        }]
      },
      // 常量
      POST_TYPE,
      POST_MAX_IMAGES,
      OFFICIAL_NICKNAME: OFFICIAL_USER.nickname
    }
  },
  watch: {
    // 传了配图 → 清空视频（互斥：图优先于视频）
    'publishForm.images'(arr) {
      if (arr && arr.length && this.publishForm.video_url) this.resetVideo()
    },
    // 传了视频 → 清空配图（互斥：视频优先于图）
    'publishForm.video_url'(val) {
      if (val && this.publishForm.images.length) this.publishForm.images = []
    }
  },
  computed: {
    filteredList() {
      const kw = (this.query.keyword || '').trim().toLowerCase()
      return this.list.filter(i => {
        if (kw) {
          const author = i.nickname || ''
          const hay = `${i.title || ''}${i.content || ''}${author}`.toLowerCase()
          if (hay.indexOf(kw) === -1) return false
        }
        if (this.query.type !== '' && this.query.type !== null && i.type !== this.query.type) return false
        return true
      })
    },
    commentTotal() {
      return this.list.reduce((sum, i) => sum + (i.comment_count || 0), 0)
    },
    // 帖子形式自动判定：无视频=图文；有视频按画面宽高比定横版(2)/竖版(3)，元信息读不到时兜底横版
    publishType() {
      const f = this.publishForm
      if (!f.video_url) return 1
      return (f.video_width > 0 && f.video_height > 0 && f.video_height > f.video_width) ? 3 : 2
    },
    publishTypeLabel() {
      return postTypeLabel(this.publishType)
    },
    publishDetectText() {
      const f = this.publishForm
      if (!f.video_url) {
        const n = (f.images || []).length
        return n ? `图文 · 配图 ${n} 张` : '图文 · 纯文字'
      }
      const dims = f.video_width > 0 && f.video_height > 0 ? ` ${f.video_width}×${f.video_height}` : ''
      const dur = formatDuration(f.duration)
      return `视频${dims}${dur ? ' · ' + dur : ''}`
    }
  },
  created() {
    this.loadData()
    this.loadActivities()
  },
  methods: {
    // ===== 常量方法（模板内直接调用） =====
    postTypeLabel,
    formatDuration,
    postMediaText,
    shortName(nickname) {
      return (nickname || '家').slice(0, 1)
    },
    activityName(activityId) {
      const item = this.activities.find(a => a.id === activityId)
      return item ? item.name : '未知活动'
    },
    typeCount(val) {
      return this.list.filter(i => i.type === val).length
    },
    // 视频封面背景（无封面时给深色底，由 CSS 兜底）
    coverStyle(row) {
      return row.cover ? { backgroundImage: `url(${row.cover})` } : {}
    },

    // ===== 视频预览 =====
    openVideo() {
      if (this.current && this.current.video_url) this.videoVisible = true
    },
    // 关弹框即停播（v-if 销毁 video 节点，声音不会残留）
    onVideoClosed() {
      this.videoVisible = false
    },

    // ===== 数据 =====
    loadData() {
      this.loading = true
      return getPostList().then(res => {
        if (res.code !== 200) return
        this.list = (res.data && res.data.list) || []
        if (this.current) {
          const hit = this.list.find(i => i.id === this.current.id)
          this.current = hit || this.list[0] || null
        } else {
          this.current = this.list[0] || null
        }
        if (this.current) this.loadComments(this.current.id)
        else this.comments = []
      }).finally(() => {
        this.loading = false
      })
    },
    loadActivities() {
      getActivityList({ page: 1, page_size: 999 }).then(res => {
        if (res.code !== 200) return
        const rows = (res.data && res.data.list) || []
        this.activities = rows.map(a => ({ id: a.id, name: a.name }))
      })
    },
    loadComments(postId) {
      this.commentsLoading = true
      return getPostDetail(postId).then(res => {
        if (res.code !== 200) {
          this.comments = []
          return
        }
        const data = res.data || {}
        this.comments = data.comments || []
        // 详情口径为准，同步留言数
        const post = this.list.find(i => i.id === postId)
        if (post && data.post) this.$set(post, 'comment_count', data.post.comment_count)
      }).finally(() => {
        this.commentsLoading = false
      })
    },
    selectPost(item) {
      this.current = item
      this.replyContent = ''
      this.comments = []
      this.loadComments(item.id)
    },
    resetFilter() {
      this.query = { keyword: '', type: '' }
    },

    // ===== 删除 =====
    handleDeletePost(row) {
      this.$confirm(
        `确认删除「${row.title || '该帖子'}」？删除后小程序端将不再展示，该帖下的 ${row.comment_count || 0} 条留言一并删除，且不可恢复。`,
        '删除帖子',
        { type: 'warning', confirmButtonText: '确认删除', confirmButtonClass: 'el-button--danger' }
      ).then(() => deletePost(row.id)).then(res => {
        if (res.code !== 200) return
        this.$message.success('已删除')
        this.loadData()
      }).catch(() => {})
    },

    // ===== 留言 =====
    handleDeleteComment(comment) {
      this.$confirm(
        `确认删除 ${comment.nickname || '用户'} 的这条留言？删除后小程序端不再展示。`,
        '删除留言',
        { type: 'warning', confirmButtonText: '确认删除', confirmButtonClass: 'el-button--danger' }
      ).then(() => deletePostComment(comment.id)).then(res => {
        if (res.code !== 200) return
        this.comments = this.comments.filter(c => c.id !== comment.id)
        this.syncCommentCount(comment.post_id, -1)
        this.$message.success('留言已删除')
      }).catch(() => {})
    },
    handleReply() {
      const content = (this.replyContent || '').trim()
      if (!content || !this.current) return
      replyPost(this.current.id, { content }).then(res => {
        if (res.code !== 200) return
        this.replyContent = ''
        this.$message.success('已以官方账号回复')
        this.loadComments(this.current.id)
      })
    },
    syncCommentCount(postId, delta) {
      const post = this.list.find(i => i.id === postId)
      if (post) this.$set(post, 'comment_count', Math.max(0, (post.comment_count || 0) + delta))
    },

    // ===== 发布 =====
    handleCreate() {
      this.publishForm = this.emptyPublishForm()
      this.publishVisible = true
      this.$nextTick(() => this.$refs.publishForm && this.$refs.publishForm.clearValidate())
    },
    onVideoDuration(seconds) {
      this.publishForm.duration = seconds || 0
    },
    // 视频元信息（宽高）→ 自动判定横版/竖版
    onVideoMeta(meta) {
      meta = meta || {}
      this.publishForm.duration = meta.duration || this.publishForm.duration || 0
      this.publishForm.video_width = meta.width || 0
      this.publishForm.video_height = meta.height || 0
    },
    // 清空视频相关字段（图/视频互斥时用；隐藏≠清值不适用——发帖表单只有新增，无编辑回显）
    resetVideo() {
      this.publishForm.video_url = ''
      this.publishForm.cover = ''
      this.publishForm.video_width = 0
      this.publishForm.video_height = 0
      this.publishForm.duration = 0
    },
    submitPublish() {
      this.$refs.publishForm.validate(valid => {
        if (!valid) return
        const form = this.publishForm
        const type = this.publishType
        this.submitting = true
        const payload = {
          type: type,
          title: form.title,
          content: form.content,
          images: type === 1 ? form.images.slice(0, POST_MAX_IMAGES) : [],
          cover: type === 1 ? '' : form.cover,
          video_url: type === 1 ? '' : form.video_url,
          duration: type === 1 ? 0 : (form.duration || 0),
          activity_id: form.activity_id || 0
        }
        createPost(payload).then(res => {
          if (res.code !== 200) return
          this.publishVisible = false
          this.$message.success('已发布，小程序端可见')
          this.query = { keyword: '', type: '' }
          this.loadData().then(() => {
            const freshId = res.data && res.data.id
            this.current = this.list.find(i => i.id === freshId) || this.list[0] || null
            this.replyContent = ''
            if (this.current) this.loadComments(this.current.id)
          })
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    emptyPublishForm() {
      return {
        title: '',
        content: '',
        images: [],
        cover: '',
        video_url: '',
        video_width: 0,
        video_height: 0,
        duration: 0,
        activity_id: 0
      }
    }
  }
}
</script>

<style scoped>
/* ===== 左右分栏骨架 ===== */
.post-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 16px;
}

.post-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.post-list-card {
  flex: 1 1 auto;
  min-width: 0;
}

.post-action-card {
  flex: 0 0 460px;
}

.post-card >>> .el-card__header {
  flex-shrink: 0;
  padding: 10px 16px;
  background: #fafafa;
}

.post-card >>> .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 16px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-head-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.card-head-extra {
  font-size: 12px;
  color: #909399;
}

/* ===== 左：筛选 ===== */
.list-filter {
  flex-shrink: 0;
  margin-bottom: 12px;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.filter-row .el-select {
  flex: 1;
  min-width: 0;
}

/* ===== 左：帖子列表 ===== */
.post-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0 -4px;
  padding: 0 4px;
}

.post-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.post-item:hover {
  border-color: #d9c7c3;
  background: #fdfbfb;
}

.post-item.is-active {
  border-color: var(--primary);
  background: #fdf6f5;
}

.item-thumb {
  position: relative;
  flex: 0 0 auto;
  width: 88px;
  height: 66px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f6f8;
  border: 1px dashed #dcdfe6;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: #c0c4cc;
}

.item-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-thumb i {
  font-size: 20px;
}

.thumb-tag {
  font-size: 11px;
}

.thumb-duration {
  position: absolute;
  right: 3px;
  bottom: 3px;
  padding: 0 4px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  line-height: 16px;
}

.item-main {
  flex: 1;
  min-width: 0;
}

.item-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-content {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  height: 36px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 6px;
}

.item-foot {
  font-size: 12px;
  color: #a8abb2;
}

.item-foot .dot {
  margin: 0 6px;
}

.item-right {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.item-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: 12px;
  color: #a8abb2;
}

.item-stats i {
  margin-right: 3px;
}

.list-empty {
  padding: 40px 0;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

.list-empty i {
  font-size: 28px;
  display: block;
  margin-bottom: 8px;
}

/* ===== 右：操作面板 ===== */
.action-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  text-align: center;
  padding: 20px;
}

.action-empty i {
  font-size: 40px;
  margin-bottom: 12px;
}

.empty-title {
  margin: 0 0 6px;
  font-size: 14px;
  color: #909399;
}

.empty-sub {
  margin: 0;
  font-size: 12px;
  color: #c0c4cc;
  line-height: 1.6;
}

.action-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0 -4px;
  padding: 0 4px;
}

.panel-block {
  padding-bottom: 14px;
}

.panel-block + .panel-block {
  border-top: 1px solid #f0f0f0;
  padding-top: 14px;
}

.block-title {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: var(--primary);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.block-title-right {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.block-title-right .text-danger-btn {
  flex: 0 0 auto;
  margin-left: 0;
}

.block-hint {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}

.avatar {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e4e7ed;
  color: #909399;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar--sm {
  width: 26px;
  height: 26px;
  font-size: 12px;
}

/* el-image 是子组件根节点才带 scoped 属性，头像图直接复用 .avatar 的圆形尺寸 */
.avatar--img {
  flex-shrink: 0;
}

.avatar--official {
  background: var(--primary);
  color: #fff;
}

.official-tag {
  flex: 0 0 auto;
  background: var(--primary);
  border-color: var(--primary);
  padding: 0 4px;
  height: 18px;
  line-height: 18px;
}

/* 帖子操作（删除帖子已固定在标题行，这里只留留言点赞样式） */

/* 留言区：管理控件（逐条删除 + 官方回复） */
.comment-like {
  font-size: 12px;
  color: #a8abb2;
}

.reply-box {
  background: #fff;
  border: 1px solid #eee6e4;
  border-radius: 6px;
  padding: 10px;
  margin: 0 0 12px;
}

.reply-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.reply-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

/* ===== 弹框 ===== */
.form-alert {
  margin-bottom: 16px;
}

.form-section {
  margin-bottom: 6px;
}

.form-section-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
  margin: 14px 0;
}

.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

/* 内容形式自动识别行 */
.detect-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.detect-text {
  font-size: 12px;
  color: #606266;
}

.post-form-dialog >>> .el-dialog__body {
  padding: 16px 30px 4px;
}

/* ===== 预览：小程序卡片 ===== */
.phone-card {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.phone-body {
  padding: 14px 16px;
}

.phone-author {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.phone-author-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.phone-author-name {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 6px;
}

.phone-time {
  font-size: 12px;
  color: #a8abb2;
}

.phone-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  line-height: 1.5;
}

.phone-content {
  margin: 0 0 12px;
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 图文：三栏图集 */
.phone-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-bottom: 12px;
}

.phone-grid-item {
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  background: #f0f0f0;
  padding-bottom: 100%;
}

/* el-image 是子组件，内部 img 拿不到 scoped 属性，必须 >>> 穿透；
   根节点整体绝对定位铺满 padding 撑出的方块，图片不会溢出、灰底不外露 */
.phone-grid-item >>> .el-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
}

/* 视频：横版 16:9 / 竖版 3:4 */
.phone-media {
  margin-bottom: 12px;
}

.phone-media-empty {
  font-size: 12px;
  color: #b9b3ab;
  background: #f6f4f1;
  border-radius: 4px;
  padding: 10px 12px;
}

.phone-video {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background: #303030 center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.phone-video--tall {
  aspect-ratio: 3 / 4;
  width: 78%;
  margin: 0 auto;
}

.phone-video i {
  color: rgba(255, 255, 255, 0.92);
  font-size: 46px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}

.phone-video-duration {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 0 6px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
}

/* 视频预览弹框 */
.video-preview {
  display: block;
  width: 100%;
  max-height: 70vh;
  background: #000;
  border-radius: 4px;
}

.video-preview-empty {
  padding: 40px 0;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

.phone-tag {
  display: inline-block;
  font-size: 12px;
  color: var(--primary);
  background: #fdf0ee;
  border-radius: 3px;
  padding: 2px 8px;
  margin-bottom: 10px;
}

.phone-stats {
  display: flex;
  gap: 20px;
  font-size: 12px;
  color: #909399;
  padding-top: 10px;
  border-top: 1px solid #f5f5f5;
}

.phone-comments {
  padding: 12px 16px 16px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.phone-comments-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.phone-comments-hint {
  font-size: 12px;
  font-weight: 400;
  color: #b9b3ab;
}

.phone-comment {
  display: flex;
  gap: 8px;
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px dashed #eee9e4;
}

.phone-comment-main {
  flex: 1;
  min-width: 0;
}

.phone-comment-name {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 6px;
}

.phone-comment-time {
  margin-left: auto;
  color: #b9b3ab;
}

.phone-comment-content {
  margin-top: 2px;
  font-size: 13px;
  color: #303133;
  line-height: 1.6;
  word-break: break-word;
}

.phone-comment-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}

.phone-comment-empty {
  font-size: 12px;
  color: #c0c4cc;
  padding-bottom: 4px;
}
</style>
